import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { AddClinicDomainRequest, CreateClinicRequest, IClinic, IClinicDomain, IClinicMember, InviteAdminRequest, UpdateClinicRequest } from "../library/models/clinic";
import { BaseService } from "./baseService";

/** The variables an invite needs: which clinic, and who to invite. */
export interface InviteAdminVariables {
    clinicId: number;
    payload: InviteAdminRequest;
}

/** Which clinic gets the hostname, and which hostname. */
export interface AddClinicDomainVariables {
    clinicId: number;
    payload: AddClinicDomainRequest;
}

/** Which clinic's hostname, and which of them. Both ids: the API scopes by tenant too. */
export interface ClinicDomainVariables {
    clinicId: number;
    domainId: number;
}

/** The variables a PATCH needs: which clinic, and what to change about it. */
export interface UpdateClinicVariables {
    id: number;
    payload: UpdateClinicRequest;
}

/**
 * The clinic registry, served by the platform endpoints. Every route here requires a
 * platform session; a clinic-scoped token gets a 403.
 *
 * This is the only file that uses the API's word for a clinic -- "tenant".
 */
export class ClinicsService extends BaseService {
    /** Includes deactivated clinics, which is what makes reactivating one possible. */
    getClinics(): UseQueryOptions<IClinic[], Error> {
        return this.createGetQuery<IClinic[]>('/platform/tenants');
    }

    createClinicMutation(): UseMutationOptions<IClinic, Error, CreateClinicRequest> {
        return this.createMutation<IClinic, CreateClinicRequest>('/platform/tenants');
    }

    /**
     * Hand-written rather than built with createMutation, which bakes its URL in at
     * construction time and so cannot put the id in the path. Taking the id as a
     * mutation variable also keeps this a single mutation for the whole list, instead of
     * one per row.
     */
    updateClinicMutation(): UseMutationOptions<IClinic, Error, UpdateClinicVariables> {
        return {
            mutationFn: ({ id, payload }: UpdateClinicVariables): Promise<IClinic> =>
                this.patch<IClinic>(`/platform/tenants/${id}`, payload),
        };
    }

    /**
     * Everyone attached to the clinic, including invitations nobody has answered yet.
     * The clinic's own admins get the same rows from UsersService.getClinicMembers,
     * without having to name the clinic.
     */
    getClinicMembers(clinicId: number): UseQueryOptions<IClinicMember[], Error> {
        return this.createGetQuery<IClinicMember[]>(`/platform/tenants/${clinicId}/users`);
    }

    /** Hand-written for the same reason as updateClinicMutation: the id is in the path. */
    inviteClinicAdminMutation(): UseMutationOptions<IClinicMember, Error, InviteAdminVariables> {
        return {
            mutationFn: ({ clinicId, payload }: InviteAdminVariables): Promise<IClinicMember> =>
                this.post<IClinicMember>(`/platform/tenants/${clinicId}/admins`, payload),
        };
    }

    /*
     * The hostnames a clinic answers on.
     *
     * All three answer with the clinic's whole list rather than with the row they touched,
     * because none of them changes only that row: making one hostname primary clears the flag
     * on whichever held it, and adding one as primary does the same. A caller that patched its
     * own state from a single row would draw two primaries.
     *
     * There is no getter -- the domains ride on `IClinic`, so the list the registry already
     * fetched is what seeds the dialog. Hand-written for the reason updateClinicMutation
     * gives: createMutation bakes its URL in at construction and cannot put an id in the path.
     */

    addClinicDomainMutation(): UseMutationOptions<IClinicDomain[], Error, AddClinicDomainVariables> {
        return {
            mutationFn: ({ clinicId, payload }: AddClinicDomainVariables): Promise<IClinicDomain[]> =>
                this.post<IClinicDomain[]>(`/platform/tenants/${clinicId}/domains`, payload),
        };
    }

    /** Which of a clinic's hostnames its emailed links name. */
    makeClinicDomainPrimaryMutation(): UseMutationOptions<IClinicDomain[], Error, ClinicDomainVariables> {
        return {
            mutationFn: ({ clinicId, domainId }: ClinicDomainVariables): Promise<IClinicDomain[]> =>
                this.put<IClinicDomain[]>(`/platform/tenants/${clinicId}/domains/${domainId}/primary`),
        };
    }

    /**
     * Removing the primary leaves the clinic with none rather than promoting a survivor, and
     * the clinic's emails fall back to the platform's address until the owner picks another.
     */
    removeClinicDomainMutation(): UseMutationOptions<IClinicDomain[], Error, ClinicDomainVariables> {
        return {
            mutationFn: ({ clinicId, domainId }: ClinicDomainVariables): Promise<IClinicDomain[]> =>
                this.delete<IClinicDomain[]>(`/platform/tenants/${clinicId}/domains/${domainId}`),
        };
    }
}
